import { VgApiResult, vgApiRequest, vgPublicRequest } from "./vg-api.service";
import type { FreeTime } from "./facilities.service";

/**
 * The marketplace: what a family sees without signing in, and the one request they send
 * to a funeral home and a resting place together. Mirrors api/Funely/Bundle.cs.
 * Providers accept (with a price) or decline; then the family pays the whole request once,
 * on Funely's Stripe account, and each provider is paid its share (api/Funely/BundlePayment.cs).
 */
export const ITEM_KIND = { FUNERAL_SERVICE: 1, GRAVE_PLOT: 2, CREMATION_TIME: 3 } as const;
export const ITEM_STATUS = { REQUESTED: 1, ACCEPTED: 2, DECLINED: 3 } as const;

export type ItemKind = (typeof ITEM_KIND)[keyof typeof ITEM_KIND];
export type ItemStatus = (typeof ITEM_STATUS)[keyof typeof ITEM_STATUS];

export const ITEM_KIND_LABEL: Record<ItemKind, string> = {
  1: "Funeral home",
  2: "Grave plot",
  3: "Cremation time",
};

export const ITEM_STATUS_LABEL: Record<ItemStatus, string> = {
  1: "Waiting for a reply",
  2: "Accepted",
  3: "Declined",
};

/** Core's ENUM_SYS_CurrencyOption: the ISO 4217 numbers. The currencies a provider can price in. */
export const CURRENCY = { GBP: 826, EUR: 978, SEK: 752, USD: 840 } as const;
export type Currency = (typeof CURRENCY)[keyof typeof CURRENCY];

const CURRENCY_CODE: Record<number, string> = { 826: "GBP", 978: "EUR", 752: "SEK", 840: "USD" };

/** An amount for people to read, e.g. "£2,500.00". */
export const formatMoney = (amount: number, currency: number): string => {
  const code = CURRENCY_CODE[currency];
  if (!code) return amount.toFixed(2);
  return new Intl.NumberFormat("en-GB", { style: "currency", currency: code }).format(amount);
};

export type BundleItem = {
  itemId: number;
  bundleId: number;
  orgId: number;
  providerName: string;
  kind: ItemKind;
  plotId: number | null;
  resourceId: number | null;
  startsAt: string;
  endsAt: string;
  description: string;
  status: ItemStatus;
  /** The provider's price, once it has accepted; 0 until then. */
  monetaryAmount: number;
  monetaryCurrency: number;
  /** The provider's share has been transferred to its Stripe account. */
  transferred: boolean;
  /** The provider has refunded this part to the family. */
  refunded: boolean;
};

/** Core's ENUM_Payment_Dispute_State: the family disputing the payment with their bank. */
export const DISPUTE = { NONE: 0, OPEN: 1, WON: 2, LOST: 3 } as const;

export type Bundle = {
  bundleId: number;
  reference: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  deceasedName: string;
  message: string;
  createdOn: string;
  /** When the family paid; empty until then. */
  paidOn: string;
  disputeState: number;
  items: BundleItem[];
};

export type ProviderRequest = {
  item: BundleItem;
  reference: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  deceasedName: string;
  message: string;
  createdOn: string;
  /** When the family paid for the request; empty until then. */
  paidOn: string;
  disputeState: number;
};

/** What the family has chosen so far. */
export type BundleDraft = {
  funeralHomeOrgId: number | null;
  plot: { orgId: number; plotId: number } | null;
  cremation: { orgId: number; resourceId: number; startsAt: string } | null;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  deceasedName: string;
  message: string;
};

export const emptyDraft = (): BundleDraft => ({
  funeralHomeOrgId: null,
  plot: null,
  cremation: null,
  contactName: "",
  contactEmail: "",
  contactPhone: "",
  deceasedName: "",
  message: "",
});

const EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

/** Why the request cannot be sent yet, or null. Same checks as BundleRules. */
export const validateDraft = (d: BundleDraft): string | null => {
  if (!d.funeralHomeOrgId && !d.plot && !d.cremation) return "Choose at least one provider.";
  if (d.plot && d.cremation) return "Choose a grave plot or a cremation time, not both.";
  if (!d.contactName.trim()) return "Tell the providers who to reply to.";
  const email = d.contactEmail.trim();
  if (!email && !d.contactPhone.trim()) return "Give an email address or a phone number so the providers can reply.";
  if (email && !EMAIL.test(email)) return "That email address does not look right.";
  return null;
};

/** The request body the API expects. */
export const toRequest = (d: BundleDraft) => ({
  contactName: d.contactName.trim(),
  contactEmail: d.contactEmail.trim(),
  contactPhone: d.contactPhone.trim(),
  deceasedName: d.deceasedName.trim(),
  message: d.message,
  items: [
    ...(d.funeralHomeOrgId ? [{ kind: ITEM_KIND.FUNERAL_SERVICE, orgId: d.funeralHomeOrgId }] : []),
    ...(d.plot ? [{ kind: ITEM_KIND.GRAVE_PLOT, orgId: d.plot.orgId, plotId: d.plot.plotId }] : []),
    ...(d.cremation
      ? [{ kind: ITEM_KIND.CREMATION_TIME, orgId: d.cremation.orgId, resourceId: d.cremation.resourceId, startsAt: d.cremation.startsAt }]
      : []),
  ],
});

type Envelope<T> = { data?: T | null };

const unwrap = <T>(result: VgApiResult<Envelope<T>>): VgApiResult<T> =>
  result.error ? { data: null, error: result.error } : { data: (result.data?.data as T) ?? null, error: null };

export type PublicFacility = { resourceId: number; resourceName: string };

/** A listed crematorium's cremators and chapels. No sign-in. */
export const readPublicFacilities = async (orgId: number): Promise<VgApiResult<PublicFacility[]>> => {
  const { data, error } = await vgPublicRequest<Record<string, unknown>>("Read_Public_Facilities", { orgId });
  if (error) return { data: null, error };
  const rows = ((data?.resourceCompleteList ?? data?.resourceList ?? data?.ResourceList ?? []) as Record<string, unknown>[]) || [];
  return {
    data: rows.map((r) => ({
      resourceId: Number(r.resourceId ?? 0),
      resourceName: String(r.resourceName ?? "").trim() || "Facility",
    })),
    error: null,
  };
};

/** A listed crematorium facility's free times between two moments (at most a week). No sign-in. */
export const readPublicFreeTimes = async (
  orgId: number,
  resourceId: number,
  from: Date,
  to: Date
): Promise<VgApiResult<FreeTime[]>> => {
  const { data, error } = await vgPublicRequest<Record<string, unknown>>("Read_Public_Free_Times", {
    orgId,
    resourceId,
    from: from.toISOString(),
    to: to.toISOString(),
  });
  if (error) return { data: null, error };
  const rows = ((data?.ListOfFreeSlots ?? data?.listOfFreeSlots ?? []) as Record<string, unknown>[]) || [];
  return {
    data: rows
      .filter((r) => (r.bookableSlot ?? true) !== false)
      .map((r) => ({ resourceId, start: String(r.start ?? ""), end: String(r.end ?? "") }))
      .filter((t) => t.start && t.end)
      .sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime()),
    error: null,
  };
};

/** Sends the family's request. No sign-in. */
export const sendRequest = async (d: BundleDraft): Promise<VgApiResult<Bundle>> => {
  const invalid = validateDraft(d);
  if (invalid) return { data: null, error: { message: invalid } };
  return unwrap(await vgPublicRequest<Envelope<Bundle>>("Create_Bundle_Request", toRequest(d)));
};

/** Where a request stands, by its reference. No sign-in. */
export const readRequest = async (reference: string): Promise<VgApiResult<Bundle>> =>
  unwrap(await vgPublicRequest<Envelope<Bundle>>("Read_Bundle_Request", { reference }));

/** The requests addressed to this provider. Members only. */
export const readProviderRequests = async (
  accessToken: string,
  orgId: number
): Promise<VgApiResult<ProviderRequest[]>> => {
  const result = unwrap(await vgApiRequest<Envelope<ProviderRequest[]>>(accessToken, "Read_Provider_Requests", { orgId }));
  return result.error ? result : { data: result.data ?? [], error: null };
};

/** Accepting needs the provider's price; declining does not. */
export type Answer =
  | { status: typeof ITEM_STATUS.ACCEPTED; monetaryAmount: number; monetaryCurrency: Currency }
  | { status: typeof ITEM_STATUS.DECLINED };

export const answerRequest = async (
  accessToken: string,
  orgId: number,
  itemId: number,
  answer: Answer
): Promise<VgApiResult<BundleItem>> =>
  unwrap(await vgApiRequest<Envelope<BundleItem>>(accessToken, "Update_Provider_Request", { orgId, itemId, ...answer }));

/**
 * Gives the family back the full price of this provider's part; the provider's share comes back
 * out of its Stripe account. Members only.
 */
export const refundRequest = async (accessToken: string, orgId: number, itemId: number): Promise<VgApiResult<BundleItem>> =>
  unwrap(await vgApiRequest<Envelope<BundleItem>>(accessToken, "Refund_Provider_Request", { orgId, itemId }));

/** Opens Stripe Checkout for the whole request; returns where to send the family. No sign-in. */
export const startCheckout = async (reference: string): Promise<VgApiResult<string>> =>
  unwrap(await vgPublicRequest<Envelope<string>>("Create_Bundle_Checkout", { reference }));
